import React, { useRef, useState } from "react";
import {
  GenericCardsStatistic,
  ContainerPanel,
  ContainerModal,
  CompsSearch,
  CompsTable,
  CompsForm,
} from "@ms/shared-components";

// 导入样式
import "./index.css";

// 导入自定义hooks
import { useTagManagement } from "./hooks/useTagManagement";
import { useTagPageConfig } from "./hooks/useTagPageConfig";

// 导入配置
import { searchItems } from "./configs/searchConfig";
import {
  createTagFormItems,
  getDefaultFormValues,
  getFormTitle,
  FORM_WIDTH,
  FORM_HEIGHT,
  FORM_LAYOUT,
} from "./configs/formConfig";
import { SEARCH_CONFIG, TABLE_CONFIG } from "./configs/tableConfig";
import { STATISTIC_CONFIG } from "./configs/statisticConfig";

const TagManagement: React.FC = () => {
  // 搜索表单引用
  const searchFormRef = useRef<any>(null);
  // 表单引用
  const formRef = useRef<any>(null);
  // 列设置按钮状态
  const [columnSettingButton, setColumnSettingButton] =
    useState<React.ReactNode>(null);

  // 使用自定义hook管理状态和逻辑
  const {
    tags,
    statistics,
    loading,
    formModalVisible,
    editingTag,
    pagination,
    loadTags,
    handleSearch,
    handleCreate,
    handleEdit,
    handleDelete,
    handleFormSuccess,
    handlePaginationChange,
    closeFormModal,
  } = useTagManagement();

  // 使用页面配置hook
  const {
    statisticData,
    searchPanelButtons,
    tableButtons,
    columns,
    tableTitle,
    handleFormSubmit,
  } = useTagPageConfig({
    searchFormRef,
    formRef,
    columnSettingButton,
    statistics,
    handleSearch,
    loadTags,
    handleCreate,
    handleEdit,
    handleDelete,
    handleFormSuccess,
    closeFormModal,
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        gap: 16,
      }}
    >
      {/* 统计卡片 */}
      <GenericCardsStatistic
        data={statisticData}
        columns={STATISTIC_CONFIG.COLUMNS}
        style={STATISTIC_CONFIG.STYLE}
      />

      {/* 搜索面板 */}
      <ContainerPanel
        title={SEARCH_CONFIG.SEARCH_TITLE}
        height="auto"
        buttons={searchPanelButtons}
      >
        <CompsSearch
          searchItems={searchItems}
          columns={SEARCH_CONFIG.DEFAULT_COLUMNS}
          onSearch={handleSearch}
          collapsible={SEARCH_CONFIG.COLLAPSIBLE}
          defaultCollapsed={SEARCH_CONFIG.DEFAULT_COLLAPSED}
          labelWidth={SEARCH_CONFIG.LABEL_WIDTH}
          showButtons={false}
          formRef={(form) => {
            searchFormRef.current = form;
          }}
        />
      </ContainerPanel>

      {/* 表格面板 */}
      <ContainerPanel
        title={tableTitle}
        buttons={tableButtons}
        height="100%"
        cardProps={{
          style: { flex: 1, display: "flex", flexDirection: "column" },
        }}
      >
        <CompsTable
          tableProps={{
            columns,
            dataSource: tags,
            rowKey: "id",
          }}
          loading={loading}
          showSelection={false}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number) => `共 ${total} 条记录`,
          }}
          onPaginationChange={handlePaginationChange}
          showColumnSetting={true}
          storageKey={TABLE_CONFIG.STORAGE_KEY}
          resizableColumns={true}
          saveColumnWidths={true}
          onColumnSettingButtonRender={setColumnSettingButton}
        />
      </ContainerPanel>

      {/* 表单弹窗 */}
      <ContainerModal
        title={getFormTitle(!!editingTag)}
        visible={formModalVisible}
        width={FORM_WIDTH}
        height={FORM_HEIGHT}
        onCancel={closeFormModal}
        footerRightButtons={[
          {
            key: "cancel",
            text: "取消",
            type: "default",
            onClick: closeFormModal,
          },
          {
            key: "ok",
            text: "确定",
            type: "primary",
            onClick: handleFormSubmit,
          },
        ]}
      >
        <CompsForm
          formItems={createTagFormItems(editingTag)}
          formRef={(form) => {
            formRef.current = form;
          }}
          initialValues={getDefaultFormValues(editingTag)}
          layout={FORM_LAYOUT.layout}
          labelCol={FORM_LAYOUT.labelCol}
          wrapperCol={FORM_LAYOUT.wrapperCol}
        />
      </ContainerModal>
    </div>
  );
};

export default TagManagement;
